import * as React from "react"
import { CircleHelp, RotateCcw } from "lucide-react"
import { FormattedMessage, useIntl } from "react-intl"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { csrfToken } from "@/lib/csrf"
import { useFormat } from "@/lib/format"
import { cn } from "@/lib/utils"
import { FlipCard } from "./FlipCard"
import type { Rating, RatingPreviews, StudyCard, Upcoming } from "@/types/flashcards"

const RATINGS: { rating: Rating; label: { id: string }; key: string }[] = [
  { rating: "again", label: { id: "flashcards.rating_again" }, key: "1" },
  { rating: "hard", label: { id: "flashcards.rating_hard" }, key: "2" },
  { rating: "good", label: { id: "flashcards.rating_good" }, key: "3" },
  { rating: "easy", label: { id: "flashcards.rating_easy" }, key: "4" },
]

/** How many cards later an "Again" card comes back in the same session. */
const AGAIN_GAP = 3

type RateResponse = { due_on: string; previews: RatingPreviews; upcoming: Upcoming }

type Result = { firstRating: Rating; dueOn: string }

/**
 * Flip-and-rate study loop shared by deck study and the daily review. Each
 * rating is saved immediately, so leaving mid-session keeps progress.
 */
export function StudySession({
  cards,
  today,
  showDeckTitle = false,
  header,
  doneActions,
}: {
  cards: StudyCard[]
  today: string
  showDeckTitle?: boolean
  header?: (controls: { shuffleRemaining: () => void }) => React.ReactNode
  doneActions: (restart: () => void) => React.ReactNode
}) {
  const intl = useIntl()
  const format = useFormat()
  const initialOrder = React.useMemo(() => cards.map((_, i) => i), [cards])
  const [queue, setQueue] = React.useState<number[]>(initialOrder)
  const [flipped, setFlipped] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [results, setResults] = React.useState<Record<number, Result>>({})
  const [previews, setPreviews] = React.useState<Record<number, RatingPreviews>>({})
  const [upcoming, setUpcoming] = React.useState<Upcoming>(null)

  const current = queue.length > 0 ? cards[queue[0]] : null
  const finished = cards.length > 0 && queue.length === 0
  const reviewedCount = Object.keys(results).length

  const restart = () => {
    setQueue(initialOrder)
    setFlipped(false)
    setError(null)
    setResults({})
  }

  const rate = React.useCallback(
    async (rating: Rating) => {
      if (!current || saving) return
      setSaving(true)
      setError(null)
      try {
        const response = await fetch("/reviews", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": csrfToken(),
          },
          body: JSON.stringify({ flashcard_id: current.id, rating }),
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data: RateResponse = await response.json()

        setResults((prev) => ({
          ...prev,
          [current.id]: { firstRating: prev[current.id]?.firstRating ?? rating, dueOn: data.due_on },
        }))
        setPreviews((prev) => ({ ...prev, [current.id]: data.previews }))
        setUpcoming(data.upcoming)
        setQueue((q) => {
          const [head, ...rest] = q
          if (rating !== "again") return rest
          const at = Math.min(AGAIN_GAP, rest.length)
          return [...rest.slice(0, at), head, ...rest.slice(at)]
        })
        setFlipped(false)
      } catch {
        setError(intl.formatMessage({ id: "flashcards.save_error" }))
      } finally {
        setSaving(false)
      }
    },
    [current, saving, intl],
  )

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!current || e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target?.closest("input, textarea, select")) return
      if (target?.closest("button, a") && (e.key === " " || e.key === "Enter")) return

      if (e.key === " ") {
        e.preventDefault()
        setFlipped((f) => !f)
        return
      }
      const match = RATINGS.find((r) => r.key === e.key)
      if (match && flipped) rate(match.rating)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [current, flipped, rate])

  if (cards.length === 0) return null

  if (finished) {
    return <SessionSummary results={results} today={today} upcoming={upcoming} actions={doneActions(restart)} />
  }

  const cardPreviews = previews[current!.id] ?? current!.previews
  const total = cards.length
  const done = Math.min(reviewedCount, total)

  return (
    <div>
      {header?.({ shuffleRemaining: () => { setFlipped(false); setQueue(shuffled) } })}

      <div className="mt-2 flex items-center gap-3">
        <div
          className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface"
          role="progressbar"
          aria-label={intl.formatMessage({ id: "flashcards.progress_label" })}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={done}
        >
          <div className="h-full bg-accent transition-[width]" style={{ width: `${(done / total) * 100}%` }} />
        </div>
        <span className="text-sm text-ink-muted">{intl.formatMessage({ id: "flashcards.left" }, { count: queue.length })}</span>
      </div>

      {showDeckTitle && current!.deck_title && (
        <div className="mt-4 text-xs text-ink-muted">{current!.deck_title}</div>
      )}

      <div className={showDeckTitle ? "mt-2" : "mt-6"}>
        <FlipCard
          key={`${current!.id}-${queue.length}`}
          front={current!.front}
          back={current!.back}
          flipped={flipped}
          onFlip={() => setFlipped((f) => !f)}
        />
      </div>

      <div className="mt-6 min-h-16">
        {flipped ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="group" aria-label={intl.formatMessage({ id: "flashcards.rate_group" })}>
            {RATINGS.map(({ rating, label, key }) => (
              <Button
                key={rating}
                type="button"
                variant={rating === "good" ? "primary" : "secondary"}
                disabled={saving}
                onClick={() => rate(rating)}
                className="h-auto flex-col gap-0.5 py-2"
                aria-keyshortcuts={key}
              >
                <span>{intl.formatMessage(label)}</span>
                <span className={cn("text-xs", rating === "good" ? "opacity-80" : "text-ink-muted")}>
                  {format.interval(cardPreviews[rating])}
                </span>
              </Button>
            ))}
          </div>
        ) : (
          <div className="flex justify-center">
            <Button type="button" onClick={() => setFlipped(true)}>
              {intl.formatMessage({ id: "flashcards.show_answer" })}
            </Button>
          </div>
        )}
        <p className="mt-3 hidden text-center text-xs text-ink-muted sm:block">
          {flipped ? intl.formatMessage({ id: "flashcards.keys_flipped" }) : intl.formatMessage({ id: "flashcards.keys_front" })}
        </p>
        {flipped && (
          <div className="mt-2 flex justify-center">
            <RatingHelp />
          </div>
        )}
        {error && (
          <div className="mt-3 flex items-center justify-center gap-3">
            <span className="text-sm text-danger-display">{error}</span>
          </div>
        )}
      </div>
    </div>
  )
}

function RatingHelp() {
  const intl = useIntl()
  const bold = { strong: (chunks: React.ReactNode) => <strong>{chunks}</strong> }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={intl.formatMessage({ id: "flashcards.help_label" })}
            className="rounded-full text-ink-muted hover:text-ink-display"
          >
            <CircleHelp className="h-4 w-4" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-sm space-y-2">
          <strong className="block">{intl.formatMessage({ id: "flashcards.help_label" })}</strong>
          <p>{intl.formatMessage({ id: "flashcards.help_intro" })}</p>
          <ul className="list-none space-y-1 pl-0">
            <li>
              <FormattedMessage id="flashcards.help_again" values={bold} />
            </li>
            <li>
              <FormattedMessage id="flashcards.help_hard" values={bold} />
            </li>
            <li>
              <FormattedMessage id="flashcards.help_good" values={bold} />
            </li>
            <li>
              <FormattedMessage id="flashcards.help_easy" values={bold} />
            </li>
          </ul>
          <p>{intl.formatMessage({ id: "flashcards.help_outro" })}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function SessionSummary({
  results,
  today,
  upcoming,
  actions,
}: {
  results: Record<number, Result>
  today: string
  upcoming: Upcoming
  actions: React.ReactNode
}) {
  const intl = useIntl()
  const format = useFormat()
  const all = Object.values(results)
  const right = all.filter((r) => r.firstRating !== "again").length

  return (
    <div className="callout mt-6 text-center">
      <h1>{intl.formatMessage({ id: "flashcards.session_complete" })}</h1>
      <p className="mt-2">
        {intl.formatMessage({ id: "flashcards.session_result" }, { count: all.length, right })}
      </p>
      <p className="mt-1">
        {upcoming
          ? intl.formatMessage(
              { id: "flashcards.session_next" },
              { when: format.relativeDay(upcoming.date, today), count: upcoming.count },
            )
          : intl.formatMessage({ id: "flashcards.session_none" })}
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">{actions}</div>
    </div>
  )
}

export function RestartButton({ onClick }: { onClick: () => void }) {
  const intl = useIntl()
  return (
    <Button type="button" variant="secondary" onClick={onClick}>
      <RotateCcw className="h-4 w-4" /> {intl.formatMessage({ id: "flashcards.study_again" })}
    </Button>
  )
}

// Only ever called from a click handler, so server rendering stays deterministic.
function shuffled(order: number[]) {
  const copy = [...order]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}
